import React from 'react';
import { Handle, Position } from 'reactflow';
import { Box, Typography, Chip, Grid, Paper } from '@mui/material';
import { BugReport, Scanner, Shield, CheckCircle } from '@mui/icons-material';

interface VulnerabilityScannerNodeProps {
  data: {
    label: string;
    config: Record<string, any>;
    status?: 'idle' | 'running' | 'success' | 'error';
  };
}

const VulnerabilityScannerNode: React.FC<VulnerabilityScannerNodeProps> = ({ data }) => {
  const getStatusColor = () => {
    switch (data.status) {
      case 'running': return '#e91e63';
      case 'success': return '#4caf50';
      case 'error': return '#f44336';
      default: return '#9c27b0';
    }
  };

  const hostsScanned = data.config?.hostsScanned || 342;
  const vulnerabilitiesFound = data.config?.vulnerabilitiesFound || 47;
  const criticalIssues = data.config?.criticalIssues || 3;

  return (
    <Box
      sx={{
        padding: 2,
        border: '2px solid',
        borderColor: getStatusColor(),
        borderRadius: 2,
        backgroundColor: 'background.paper',
        minWidth: 300,
        boxShadow: 4,
        background: 'linear-gradient(135deg, #fce4ec 0%, #f8bbd9 100%)',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <BugReport sx={{ color: 'secondary.main' }} />
        <Box>
          <Typography variant="subtitle2" fontWeight="bold">
            {data.label}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Vulnerability Assessment
          </Typography>
        </Box>
      </Box>

      <Grid container spacing={1} sx={{ mb: 2 }}>
        <Grid item xs={4}>
          <Paper elevation={1} sx={{ p: 1, textAlign: 'center' }}>
            <Scanner sx={{ fontSize: 16, color: 'info.main', mb: 0.5 }} />
            <Typography variant="caption" color="text.secondary" display="block">
              Hosts
            </Typography>
            <Typography variant="body2" fontWeight="bold" color="info.main">
              {hostsScanned}
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={4}>
          <Paper elevation={1} sx={{ p: 1, textAlign: 'center' }}>
            <Shield sx={{ fontSize: 16, color: 'warning.main', mb: 0.5 }} />
            <Typography variant="caption" color="text.secondary" display="block">
              Vulns
            </Typography>
            <Typography variant="body2" fontWeight="bold" color="warning.main">
              {vulnerabilitiesFound}
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={4}>
          <Paper elevation={1} sx={{ p: 1, textAlign: 'center' }}>
            <CheckCircle sx={{ fontSize: 16, color: 'error.main', mb: 0.5 }} />
            <Typography variant="caption" color="text.secondary" display="block">
              Critical
            </Typography>
            <Typography variant="body2" fontWeight="bold" color="error.main">
              {criticalIssues}
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      <Box sx={{ mb: 2 }}>
        <Typography variant="caption" fontWeight="medium" color="text.primary" sx={{ mb: 1, display: 'block' }}>
          Scan Types:
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
          {['Network Scan', 'Web App', 'Database', 'Config Audit'].map((scanType, index) => (
            <Chip
              key={index}
              label={scanType}
              size="small"
              variant="outlined"
              sx={{
                fontSize: '0.7rem',
                height: 24,
                borderColor: 'secondary.main',
                color: 'secondary.main',
              }}
            />
          ))}
        </Box>
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Chip
            label="✨ Enhanced"
            size="small"
            sx={{
              background: 'linear-gradient(45deg, #9c27b0, #7b1fa2)',
              color: 'white',
              fontSize: '0.7rem',
              fontWeight: 'bold',
            }}
          />
          <Chip
            label="🔍 Scanner"
            size="small"
            sx={{
              background: 'linear-gradient(45deg, #e91e63, #c2185b)',
              color: 'white',
              fontSize: '0.7rem',
              fontWeight: 'bold',
            }}
          />
        </Box>
        {data.status && (
          <Chip
            label={data.status}
            size="small"
            sx={{
              backgroundColor: getStatusColor(),
              color: 'white',
              fontSize: '0.7rem',
            }}
          />
        )}
      </Box>
      
      <Handle
        type="target"
        position={Position.Left}
        style={{
          background: '#9c27b0',
          width: 12,
          height: 12,
        }}
      />
      
      <Handle
        type="source"
        position={Position.Right}
        style={{
          background: '#9c27b0',
          width: 12,
          height: 12,
        }}
      />
    </Box>
  );
};

export default VulnerabilityScannerNode;